// 6-stem audio mixer using Web Audio per-stem gain nodes, with real pitch
// shifting (transpose) + pitch-preserving speed via a SoundTouch AudioWorklet —
// the same processor the Chrome extension uses. Per stem the graph is:
//   <audio> → MediaElementSource → SoundTouchNode → GainNode → master → out
// SoundTouch owns pitch: `pitchSemitones` = transpose, and its `playbackRate`
// param matches the element's playbackRate so changing speed shifts tempo
// without changing pitch. The worklet source is bundled and loaded via a blob
// URL (no extra deploy step); if it fails to load we fall back to a plain graph
// (speed still works via the element's native preservesPitch; transpose no-ops).
import soundtouchSrc from './soundtouch-processor.js?raw';

export type StemName = 'vocals' | 'drums' | 'bass' | 'piano' | 'guitar' | 'other';
export const STEMS: StemName[] = ['vocals', 'drums', 'bass', 'piano', 'guitar', 'other'];

export interface StemState {
  volume: number;     // 0..1 fader position
  solo: boolean;
  mute: boolean;
}

export interface MixState {
  stems: Record<StemName, StemState>;
  speed: number;
  transpose: number; // semitones, -12..+12
  loop: boolean;
  playing: boolean;
}

export const DEFAULT_MIX: MixState = {
  stems: Object.fromEntries(
    STEMS.map((s) => [s, { volume: s === 'vocals' ? 1.0 : 0.0, solo: false, mute: false }])
  ) as Record<StemName, StemState>,
  speed: 1,
  transpose: 0,
  loop: false,
  playing: false,
};

export const MIX_PRESETS: { id: string; label: string; mix: Record<StemName, number> }[] = [
  {
    id: 'vocals-only',
    label: 'Vocals only',
    mix: { vocals: 1, drums: 0, bass: 0, piano: 0, guitar: 0, other: 0 },
  },
  {
    id: 'instrumental',
    label: 'Instrumental',
    mix: { vocals: 0, drums: 1, bass: 1, piano: 1, guitar: 1, other: 1 },
  },
  {
    id: 'karaoke',
    label: 'Karaoke',
    mix: { vocals: 0.15, drums: 1, bass: 1, piano: 1, guitar: 1, other: 1 },
  },
  {
    id: 'rhythm-only',
    label: 'Rhythm only',
    mix: { vocals: 0, drums: 1, bass: 1, piano: 0, guitar: 0, other: 0 },
  },
];

// Compute audible volume given a stem's state + global solo state.
// SSL/Logic convention: if any stem is soloed, only soloed stems play.
/** Soft ceiling for the SUMMED output.
 *
 *  A WaveShaper's curve spans input -1..1 and CLAMPS anything past it, so the
 *  bus is halved before the shaper (LIMITER_PREGAIN) and the curve undoes that
 *  factor. Net result: unity gain below the knee — ordinary programme material
 *  passes untouched — a soft bend above it, and 2x of headroom before anything
 *  is clamped at all. */
const LIMITER_PREGAIN = 0.5;
function makeLimiterCurve(): Float32Array<ArrayBuffer> {
  const N = 8193; // odd -> exact zero at centre, no DC offset
  const KNEE = 0.86, CEIL = 0.995;
  const c = new Float32Array(new ArrayBuffer(N * 4));
  for (let i = 0; i < N; i++) {
    const u = ((i / (N - 1)) * 2 - 1) / LIMITER_PREGAIN; // back to real scale
    const a = Math.abs(u);
    c[i] = a <= KNEE ? u : Math.sign(u) * (KNEE + (CEIL - KNEE) * Math.tanh((a - KNEE) / (CEIL - KNEE)));
  }
  return c;
}

export function audibleVolume(stem: StemState, anySolo: boolean): number {
  if (stem.mute) return 0;
  if (anySolo && !stem.solo) return 0;
  return stem.volume;
}

export class StemEngine {
  private ctx: AudioContext | null = null;
  private elements: Partial<Record<StemName, HTMLAudioElement>> = {};
  private gains: Partial<Record<StemName, GainNode>> = {};
  // Per-stem stereo placement. Always present in the graph but created at
  // pan=0, which is a transparent pass-through — callers that never touch
  // setPan() get byte-identical audio to before panning existed.
  private panners: Partial<Record<StemName, StereoPannerNode>> = {};
  private masterGain: GainNode | null = null;
  private outBus: GainNode | null = null;
  private masterVol = 1;
  // SoundTouch pitch/tempo node per stem (present only when the worklet loaded).
  private stNodes: Partial<Record<StemName, AudioWorkletNode>> = {};
  private pitchReady = false;
  // Latest FIFO occupancy reported by the pitch worklet — the audible lag of
  // stem audio behind the element clock. Read by pipeDelaySec().
  private stLatFrames = { i: 0, o: 0 };

  // Window-relative bounds in seconds. Defaults to full track.
  private startAt = 0;
  private endAt: number | null = null;
  private loop = false;

  private onTimeUpdate?: (t: number, dur: number) => void;
  private onEnded?: () => void;

  setCallbacks(opts: { onTimeUpdate?: (t: number, dur: number) => void; onEnded?: () => void }) {
    this.onTimeUpdate = opts.onTimeUpdate;
    this.onEnded = opts.onEnded;
  }

  async load(urls: Record<StemName, string>) {
    this.dispose();
    this.ctx = new AudioContext();
    this.masterGain = this.ctx.createGain();
    // OUTPUT LIMITER. The song and the click are summed here, and the click
    // deliberately bypasses masterGain (mute/solo must never silence it), so
    // their sum can exceed full scale and the device would hard-clip it —
    // heard as crackle on every hit. This soft-clips the SUM instead, which
    // is what lets the click run at full scale and actually be loud.
    // oversample MUST stay 'none': 2x/4x add 128/192 frames of latency and
    // that is exactly what threw the click out of time before.
    const shaper = this.ctx.createWaveShaper();
    shaper.curve = makeLimiterCurve();
    shaper.oversample = 'none';
    shaper.connect(this.ctx.destination);
    // Everything enters through this gain, which is what buys the headroom.
    this.outBus = this.ctx.createGain();
    this.outBus.gain.value = LIMITER_PREGAIN;
    this.outBus.connect(shaper);
    this.masterGain.connect(this.outBus);

    // Load the SoundTouch worklet (inlined → blob URL, no separate asset to
    // deploy). If it fails, the graph still works without pitch shifting.
    this.pitchReady = false;
    try {
      const blobUrl = URL.createObjectURL(new Blob([soundtouchSrc], { type: 'application/javascript' }));
      await this.ctx.audioWorklet.addModule(blobUrl);
      URL.revokeObjectURL(blobUrl);
      this.pitchReady = true;
    } catch {
      this.pitchReady = false; // transpose disabled; speed still works
    }

    const loads = STEMS.map((stem) => {
      const url = urls[stem];
      if (!url) return Promise.resolve();
      const el = new Audio();
      el.crossOrigin = 'anonymous';
      el.preload = 'auto';
      el.src = url;
      const g = this.ctx!.createGain();
      const src = this.ctx!.createMediaElementSource(el);
      // Route through SoundTouch for transpose; bypass if the worklet is absent.
      let stNode: AudioWorkletNode | null = null;
      if (this.pitchReady) {
        try {
          el.preservesPitch = false; // SoundTouch owns pitch
          stNode = new AudioWorkletNode(this.ctx!, 'soundtouch-processor', {
            numberOfInputs: 1,
            numberOfOutputs: 1,
            outputChannelCount: [2],
          });
          src.connect(stNode);
          stNode.connect(g);
          // Every stem's pipe holds a near-identical amount; last write wins.
          stNode.port.onmessage = (e: MessageEvent) => {
            const d = e.data as { t?: string; i?: number; o?: number } | null;
            if (d && d.t === 'lat') { this.stLatFrames.i = d.i || 0; this.stLatFrames.o = d.o || 0; }
          };
        } catch {
          stNode = null;
          src.connect(g);
        }
      } else {
        src.connect(g);
      }
      let pan: StereoPannerNode | null = null;
      try {
        pan = this.ctx!.createStereoPanner();
        g.connect(pan);
        pan.connect(this.masterGain!);
      } catch {
        pan = null; // very old Safari — fall back to the direct connection
        g.connect(this.masterGain!);
      }
      g.gain.value = 0;
      this.elements[stem] = el;
      this.gains[stem] = g;
      if (pan) this.panners[stem] = pan;
      if (stNode) this.stNodes[stem] = stNode;

      return new Promise<void>((resolve) => {
        const done = () => {
          el.removeEventListener('canplay', done);
          el.removeEventListener('error', done);
          resolve();
        };
        el.addEventListener('canplay', done, { once: true });
        el.addEventListener('error', done, { once: true });
      });
    });

    await Promise.all(loads);

    const primary = this.elements.vocals || Object.values(this.elements)[0];
    if (primary) {
      primary.addEventListener('timeupdate', () => {
        const t = primary.currentTime;
        const dur = primary.duration;
        if (this.endAt != null && t >= this.endAt) {
          if (this.loop) {
            this.seekAll(this.startAt);
            this.playAll();
          } else {
            this.pauseAll();
            this.onEnded?.();
          }
        }
        this.onTimeUpdate?.(t, dur);
      });
    }
  }

  applyMix(mix: MixState) {
    const anySolo = Object.values(mix.stems).some((s) => s.solo);
    const now = this.ctx?.currentTime ?? 0;
    for (const stem of STEMS) {
      const g = this.gains[stem];
      const el = this.elements[stem];
      if (!g || !el) continue;
      const v = audibleVolume(mix.stems[stem], anySolo);
      g.gain.value = v;
      el.playbackRate = mix.speed;
      const st = this.stNodes[stem];
      if (st) {
        // SoundTouch owns pitch: transpose by `transpose` semitones and feed it
        // the playbackRate so it undoes the speed-induced pitch change → speed
        // changes tempo only, transpose changes pitch only.
        el.preservesPitch = false;
        st.parameters.get('pitchSemitones')?.setValueAtTime(mix.transpose, now);
        st.parameters.get('playbackRate')?.setValueAtTime(mix.speed, now);
      } else {
        el.preservesPitch = true; // no worklet → keep speed pitch-preserving natively
      }
    }
    this.loop = mix.loop;
  }

  /** Stereo placement for one stem, -1 (hard left) … 0 (centre) … +1 (right). */
  setPan(stem: StemName, value: number) {
    const p = this.panners[stem];
    if (p) p.pan.value = Math.max(-1, Math.min(1, value));
  }

  /**
   * Output level for the whole mix, 0…1. Sits after the per-stem gains, so it
   * scales everything without disturbing the balance the user set. Nothing
   * calls this unless a surface opts in, and the node defaults to 1 (unity).
   */
  setMasterVolume(value: number) {
    this.masterVol = Math.max(0, Math.min(1, value));
    if (this.masterGain && this.ctx) {
      // Ducks schedule ramps on this param; a fader move must win over them.
      this.masterGain.gain.cancelScheduledValues(this.ctx.currentTime);
      this.masterGain.gain.value = this.masterVol;
    }
  }

  /** Sidechain-style dip of the whole song under a metronome click scheduled
      at `at` (this context's clock). The click bypasses the master bus so it
      can't be silenced by the mix — which also means the only way to make it
      read louder WITHOUT clipping it is to lower the music around the hit.
      Depth is the ONLY honest lever left for click loudness: the click is
      already at full scale into the limiter, so "louder" now means lowering
      the music around it. 0.44 = −7.1 dB, ~30% more separation than the
      −4 dB it started at. */
  duckAt(at: number) {
    if (!this.masterGain || !this.ctx) return;
    const g = this.masterGain.gain;
    const base = this.masterVol;
    if (base <= 0.001) return;
    const dip = base * 0.44;
    const t0 = Math.max(this.ctx.currentTime, at - 0.02);
    g.setValueAtTime(base, t0);
    g.linearRampToValueAtTime(dip, Math.max(t0 + 0.001, at - 0.002));
    g.setValueAtTime(dip, at + 0.07);
    // Slower recovery than the dip: the ear reads it as space around the hit
    // rather than as the music glitching.
    g.linearRampToValueAtTime(base, at + 0.16);
  }

  setWindow(start: number, end: number) {
    this.startAt = start;
    this.endAt = end;
    this.seekAll(start);
  }

  // Re-bound playback/loop WITHOUT yanking the playhead. Used for live trim
  // preview: dragging the handles changes where playback loops, but we only
  // re-seek when the current position has fallen outside the new window.
  setBounds(start: number, end: number) {
    this.startAt = start;
    this.endAt = end;
    const el = this.elements.vocals || Object.values(this.elements)[0];
    const t = el ? el.currentTime : 0;
    if (t < start || t > end) this.seekAll(start);
  }

  /**
   * Live position off the clock element. `onTimeUpdate` rides the media
   * element's `timeupdate` event, which only fires ~4×/s — enough for a clock
   * readout, far too coarse for a moving playhead. A surface that wants smooth
   * motion can poll this from rAF instead. Read-only; nothing changes here for
   * callers that don't.
   */
  get currentTime() {
    const el = this.elements.vocals || Object.values(this.elements)[0];
    return el ? el.currentTime : 0;
  }

  /** The engine's own output context — the beta metronome schedules clicks
      here so click and stems share one output-latency path. Null before load. */
  /** Where anything that must not be muted by the mix should connect: the
      limiter, not the raw destination. */
  get outputNode(): AudioNode | null {
    return this.outBus;
  }

  get clickContext(): AudioContext | null {
    return this.ctx;
  }

  /** Audible lag of stem audio behind the element clock: whatever is sitting
      in the pitch pipe's FIFOs right now. 0 on the no-worklet fallback. The
      input-side share is in source time, so it stretches with 1/speed. */
  pipeDelaySec(speed: number): number {
    if (!this.ctx || !this.pitchReady) return 0;
    const sr = this.ctx.sampleRate || 44100;
    return (this.stLatFrames.i / Math.max(0.25, speed || 1) + this.stLatFrames.o) / sr;
  }

  seekAll(t: number) {
    for (const el of Object.values(this.elements)) {
      if (el) el.currentTime = t;
    }
  }

  async play() {
    if (!this.ctx) return;
    if (this.ctx.state === 'suspended') await this.ctx.resume();
    this.playAll();
  }

  playAll() {
    for (const el of Object.values(this.elements)) {
      if (el) el.play().catch(() => {});
    }
  }

  pause() {
    this.pauseAll();
  }

  pauseAll() {
    for (const el of Object.values(this.elements)) {
      if (el) el.pause();
    }
  }

  dispose() {
    for (const el of Object.values(this.elements)) {
      if (el) {
        el.pause();
        el.src = '';
      }
    }
    for (const n of Object.values(this.stNodes)) {
      try {
        n?.disconnect();
      } catch {
        /* ignore */
      }
    }
    for (const p of Object.values(this.panners)) {
      try {
        p?.disconnect();
      } catch {
        /* ignore */
      }
    }
    this.elements = {};
    this.gains = {};
    this.panners = {};
    this.stNodes = {};
    this.pitchReady = false;
    if (this.ctx) {
      this.ctx.close().catch(() => {});
    }
    this.ctx = null;
    this.masterGain = null;
    this.outBus = null;
  }
}
